import { useQuery } from "convex/react";

import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";

export interface AttachmentPreviewProps {
  emailId: Id<"emails">;
  storageId: Id<"_storage">;
  filename: string;
  contentType: string;
  size: number;
}

export function AttachmentPreview({
  emailId,
  storageId,
  filename,
  contentType,
  size,
}: AttachmentPreviewProps) {
  const url = useQuery(api.emails.getAttachmentUrl, { emailId, storageId });

  // undefined = loading, null = failed to resolve
  if (url === undefined) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-black/5 px-3 py-2 text-sm dark:bg-white/10">
        <span className="text-black/40 dark:text-white/40">Loading {filename}...</span>
      </div>
    );
  }

  if (url === null) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-black/5 px-3 py-2 text-sm dark:bg-white/10">
        <span className="text-black/40 dark:text-white/40">📎 {filename} (unavailable)</span>
      </div>
    );
  }

  const sizeLabel = size > 1_048_576
    ? `${(size / 1_048_576).toFixed(1)}MB`
    : `${Math.round(size / 1024)}KB`;

  // Image
  if (contentType.startsWith("image/")) {
    return (
      <div className="mt-2">
        <a href={url} target="_blank" rel="noopener noreferrer">
          <img
            src={url}
            alt={filename}
            className="max-h-96 rounded-lg border border-black/10 dark:border-white/10"
          />
        </a>
        <p className="mt-1 text-xs text-black/40 dark:text-white/40">
          {filename} ({sizeLabel})
        </p>
      </div>
    );
  }

  // PDF
  if (contentType === "application/pdf") {
    return (
      <div className="mt-2">
        <iframe
          src={url}
          title={filename}
          className="h-[500px] w-full rounded-lg border border-black/10 dark:border-white/10"
        />
        <p className="mt-1 text-xs text-black/40 dark:text-white/40">
          {filename} ({sizeLabel})
        </p>
      </div>
    );
  }

  // Audio
  if (contentType.startsWith("audio/")) {
    return (
      <div className="mt-2">
        <audio controls src={url} className="w-full">
          <track kind="captions" />
        </audio>
        <p className="mt-1 text-xs text-black/40 dark:text-white/40">
          {filename} ({sizeLabel})
        </p>
      </div>
    );
  }

  // Video
  if (contentType.startsWith("video/")) {
    return (
      <div className="mt-2">
        <video controls src={url} className="max-h-96 w-full rounded-lg">
          <track kind="captions" />
        </video>
        <p className="mt-1 text-xs text-black/40 dark:text-white/40">
          {filename} ({sizeLabel})
        </p>
      </div>
    );
  }

  // Text
  if (contentType.startsWith("text/")) {
    return (
      <div className="mt-2">
        <iframe
          src={url}
          title={filename}
          className="h-64 w-full rounded-lg border border-black/10 bg-white dark:border-white/10 dark:bg-black"
        />
        <p className="mt-1 text-xs text-black/40 dark:text-white/40">
          {filename} ({sizeLabel})
        </p>
      </div>
    );
  }

  // Fallback: download link
  return (
    <div className="mt-2">
      <a
        href={url}
        download={filename}
        className="inline-flex items-center gap-2 rounded-lg bg-black/5 px-3 py-2 text-sm text-black/70 hover:bg-black/10 dark:bg-white/10 dark:text-white/70 dark:hover:bg-white/15"
      >
        📎 {filename}
        <span className="text-black/40 dark:text-white/40">({sizeLabel})</span>
      </a>
    </div>
  );
}
